<template>
  <div class="graph-section">
    <!-- 홈 버튼 -->
    <div class="nav-top">
      <router-link to="/" class="home-button">🏠 홈으로</router-link>
    </div>

    <!-- 날짜 선택기 -->
    <div class="date-picker">
      <label for="dateInput">🗓️ 날짜 선택 : </label>
      <input
        id="dateInput"
        type="date"
        v-model="selectedDate"
        :min="'2024-01-04'"
        :max="'2025-05-31'"
      />
    </div>

    <!-- 날짜 선택 전 메시지 -->
    <div v-if="!selectedDate" class="empty-state">
      <p class="empty-message">날짜를 선택하면 시간대별 TEC 그래프가 여기에 표시됩니다 😊</p>
    </div>

    <!-- 날짜 선택 후 그래프 영역 -->
    <div v-else>
      <h2 class="graph-title">{{ selectedDate }} TEC Graphs</h2>

      <div class="hour-slider">
        <label for="hourRange">시간: {{ selectedHour }}시</label>
        <input
          id="hourRange"
          type="range"
          min="0"
          max="22"
          step="2"
          v-model="selectedHour"
          :style="sliderStyle"
        />
      </div>

      <div v-if="showModal" class="modal-backdrop" @click="closeModal">
        <img :src="modalImageSrc" class="modal-image" @error="handleModalImageError" />
      </div>

      <!-- 첫 줄 -->
      <div class="graph-grid first-row">
        <div class="graph-cell" v-for="title in ['Init_Az', 'ML_Az', 'GIMs']" :key="title">
          <img
            :src="getImagePath(title)"
            :alt="title"
            class="tec-image"
            @click="openModal(title)"
            @error="handleImageError"
          />
          <p class="image-label">{{ labelMap[title] }}의 TEC</p>
        </div>
      </div>

      <!-- 두 번째 줄 -->
      <div class="graph-grid second-row">
        <div class="graph-cell" v-for="title in ['GIM_Init', 'GIM_ML']" :key="title">
          <img
            :src="getImagePath(title)"
            :alt="title"
            class="tec-image"
            @click="openModal(title)"
            @error="handleImageError"
          />
          <p class="image-label">{{ labelMap[title] }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const selectedDate = ref(null)
const selectedHour = ref(0)
const formattedHour = computed(() => selectedHour.value.toString().padStart(2, '0'))

const labelMap = {
  Init_Az: 'Nequick Global Model',
  ML_Az: 'Nequick Korea Model',
  GIMs: 'GIMs',
  GIM_ML: 'GIMs - Nequick Korea TECU error',
  GIM_Init: 'GIMs - Nequick Global TECU error'
}

const getImagePath = (title) => {
  const date = selectedDate.value
  const hour = formattedHour.value
  if (title === 'GIM_ML' || title === 'GIM_Init') {
    return `/tec_maps_by_hour/${date}/${date}_${hour}UT_${title}.png`
  }
  return `/tec_maps_by_hour/${date}/${hour}UT_${title}_TEC.png`
}

const handleImageError = (event) => {
  const img = event.target
  const src = img.src
  const match = src.match(/\/(\d{4}-\d{2}-\d{2})\/(\d{2}UT_.+_TEC\.png)$/)
  if (match) {
    const [, date, filename] = match
    img.src = `/tec_maps_by_hour/${date}/${date}_${filename}`
  }
}

const handleModalImageError = (event) => {
  const img = event.target
  const src = img.src
  const match = src.match(/\/(\d{4}-\d{2}-\d{2})\/(\d{2}UT_.+_TEC\.png)$/)
  if (match) {
    const [, date, filename] = match
    img.src = `/tec_maps_by_hour/${date}/${date}_${filename}`
  }
}

const sliderStyle = computed(() => {
  const percent = (selectedHour.value / 22) * 100
  return {
    background: `linear-gradient(to right, #4f7bff 0%, #4f7bff ${percent}%, #333 ${percent}%, #333 100%)`
  }
})

const showModal = ref(false)
const modalImageSrc = ref('')
const openModal = (title) => {
  modalImageSrc.value = getImagePath(title)
  showModal.value = true
}
const closeModal = () => {
  showModal.value = false
}
</script>

<style scoped>
.graph-section {
  padding: 3rem 2rem;
  background: linear-gradient(145deg, #0c0c1d, #14142a);
  color: #fff;
  min-height: 100vh;
  font-family: 'Outfit', sans-serif;
}
.nav-top {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem;
}
.home-button {
  background-color: rgba(75, 85, 255, 0.15);
  color: #ffffff;
  border: 1px solid #4f7bff;
  padding: 0.5rem 1.1rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  backdrop-filter: blur(6px);
  box-shadow: 0 4px 12px rgba(0, 128, 255, 0.3);
  transition: all 0.3s ease;
}
.home-button:hover {
  background-color: rgba(79, 123, 255, 0.3);
  transform: scale(1.03);
}
.date-picker {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 1rem auto 2rem;
  color: #e0e4ff;
}
.date-picker input[type='date'] {
  background-color: rgba(27, 30, 48, 0.6);
  color: #ffffff;
  border: 1px solid #4f7bff;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  box-shadow: 0 0 8px rgba(0, 128, 255, 0.3);
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;
}
.date-picker input[type='date']:hover {
  border-color: #7aa5ff;
}
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 6rem;
  text-align: center;
  animation: fadeIn 0.8s ease;
}
.empty-message {
  font-size: 1.25rem;
  color: #ccccff;
  font-weight: 500;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.graph-title {
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1rem;
}
.hour-slider {
  max-width: 600px;
  margin: 0 auto 2rem;
  color: #eee;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.hour-slider input[type='range'] {
  -webkit-appearance: none;
  width: 100%;
  max-width: 500px;
  height: 12px;
  border-radius: 6px;
  outline: none;
  background: none;
  cursor: pointer;
}
.hour-slider input[type='range']::-webkit-slider-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(145deg, #4f7bff, #1d2b64);
  border: 2px solid white;
  box-shadow: 0 0 6px #4f7bff;
  margin-top: -6px;
}
.graph-grid.first-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  justify-content: center;
  align-items: flex-start;
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
}
.graph-grid.second-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  justify-content: center;
  align-items: flex-start;
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
}
.graph-cell {
  text-align: center;
}
.tec-image {
  width: 100%;
  max-width: 260px;
  border-radius: 10px;
  box-shadow: 0 0 12px rgba(0, 128, 255, 0.25);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: zoom-in;
}
.tec-image:hover {
  transform: scale(1.05);
  box-shadow: 0 0 16px rgba(0, 128, 255, 0.5);
}
.image-label {
  font-size: 0.95rem;
  color: #cfd8ff;
}
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.modal-image {
  max-width: 95vw;
  max-height: 95vh;
  border-radius: 12px;
  box-shadow: 0 0 24px rgba(255, 255, 255, 0.3);
}
</style>
